import { brandOf, tooltipOf, mapCanvas, mapPaletteColor } from '@orbit-mind/domain';
/**
 * The scene model.
 *
 * A document says what a map *is*; a scene says what it *looks like*. The renderer, the layout
 * engine, the hit-tester and every exporter read this one structure, which is the decision that
 * makes "export re-renders from the scene model, never a screenshot" possible at all — a PDF and
 * a canvas are two ways of drawing the same scene, not two implementations of drawing a map.
 *
 * Plain data, no framework, no DOM. It is built from a document plus a text measurer, and
 * nothing in it knows whether it will end up as SVG elements, canvas draw calls, or a page.
 *
 * Rebuilt on demand rather than cached in the engine, because the expensive part — measuring
 * text — is already cached one layer down, keyed by the string rather than by the node. A node
 * that moved does not re-measure; a node renamed to something another node already says does not
 * re-measure either.
 */
import { contentOf, mapLinkOf, resolveChart, toPlainText, } from '@orbit-mind/domain';
import { DEFAULT_TEXT_STYLE } from './measure';
import { LIST_INDENT } from '@orbit-mind/layout-engine';
import { DEFAULT_NODE_SHAPE, isStructureLayout } from '@orbit-mind/shared-types';
import { fishboneRoute, listRoute, markerAngle, route, routingForLayout, simpleRoute, underlineOutline, } from './routing';
import { CONTENT_GAP, CONTENT_INSET, LINK_HEIGHT, contentPrimitives, contentSize, linkPrimitives, linkWidth, } from './content';
import { drawChart } from './charts';
import { outlineFor, paddingScaleFor } from './shapes';
import { primitivesBounds, structureScene } from './structures';
export const DEFAULT_MAX_TEXT_WIDTH = 220;
export const DEFAULT_NODE_PADDING = { x: 12, y: 8 };
/** Shapes with room for a rectangle of content inside them. */
const BOXY_SHAPES = new Set([
    'rectangle',
    'rounded-rectangle',
    'square',
    'card',
    'sticky-note',
    'invisible-frame',
]);
/** Minimums, so an empty node is still a target a person can click. */
export const MIN_NODE_WIDTH = 40;
export const MIN_NODE_HEIGHT = 28;
/**
 * How round a box's corners are.
 *
 * An explicit `cornerRadius` on the style wins — it is the user saying a number. Otherwise the
 * shape decides: square corners for a rectangle, half the height for a capsule, and a modest
 * radius for everything else, which is what the remaining shapes fall back to until each has
 * geometry of its own in every renderer.
 */
export function cornerRadiusFor(shape, style, height) {
    if (style.cornerRadius !== undefined)
        return style.cornerRadius;
    if (shape === 'rectangle' || shape === 'square')
        return 0;
    if (shape === 'capsule' || shape === 'circle' || shape === 'ellipse')
        return height / 2;
    return 9;
}
function textStyleFor(style, base) {
    return {
        fontFamily: style.fontFamily ?? base.fontFamily,
        fontSize: style.fontSize ?? base.fontSize,
        fontWeight: style.fontWeight ?? base.fontWeight,
        italic: style.italic ?? base.italic,
    };
}
/**
 * Which nodes are visible.
 *
 * A collapsed node hides its descendants — all of them, not just its children, which is why this
 * walks from the root rather than filtering the node table. Filtering would keep a grandchild
 * whose parent is hidden but whose grandparent is not, and it would render at coordinates that
 * belong to a branch nobody can see.
 *
 * Returns depths too, because the renderer colours by depth and computing it separately would
 * walk the tree a second time.
 */
export function visibleNodes(document) {
    const childrenOf = new Map();
    for (const node of Object.values(document.nodes)) {
        if (node.parentId === null)
            continue;
        const siblings = childrenOf.get(node.parentId) ?? [];
        siblings.push(node);
        childrenOf.set(node.parentId, siblings);
    }
    // Sibling order is the fractional index, compared as strings — that is what the key is for.
    for (const siblings of childrenOf.values()) {
        siblings.sort((a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : 0));
    }
    const visible = new Map();
    const root = document.nodes[document.map.rootNodeId];
    if (root === undefined)
        return visible;
    const stack = [{ node: root, depth: 0 }];
    while (stack.length > 0) {
        const current = stack.pop();
        if (current === undefined)
            break;
        if (current.node.hidden)
            continue;
        visible.set(current.node.id, current.depth);
        if (current.node.collapsed)
            continue;
        // Pushed in reverse so the stack pops them in sibling order. A `Map` keeps insertion order,
        // so what comes out of this is the outline: parent, then its children, depth first. Range
        // selection ranges over exactly that sequence, and it has to be the one the user can see.
        const children = childrenOf.get(current.node.id) ?? [];
        for (let index = children.length - 1; index >= 0; index -= 1) {
            const child = children[index];
            if (child !== undefined)
                stack.push({ node: child, depth: current.depth + 1 });
        }
    }
    return visible;
}
/** The visible nodes in outline order — parent, then each child's subtree, depth first. */
export function visibleOrder(document) {
    return [...visibleNodes(document).keys()];
}
/**
 * The layout id in force at a node: its own, or the nearest ancestor's, or the map's.
 *
 * Memoised, so a deep map costs one walk rather than one per link. Asked about a *parent* when
 * drawing the link or the node below it: a family belongs to the branch it arranges, and the
 * node that carries it is the first node of that branch, not a member of the one it hangs from.
 */
function familyResolver(document) {
    const cache = new Map();
    return (nodeId) => {
        const cached = cache.get(nodeId);
        if (cached !== undefined)
            return cached;
        let current = nodeId;
        let found = document.map.layoutId;
        while (current !== null) {
            const ancestor = document.nodes[current];
            if (ancestor === undefined)
                break;
            if (ancestor.layoutId !== undefined) {
                found = ancestor.layoutId;
                break;
            }
            current = ancestor.parentId;
        }
        cache.set(nodeId, found);
        return found;
    };
}
/** A family whose nodes are text on a line rather than boxes. */
function isSimpleFamily(layoutId) {
    return layoutId.startsWith('simple-');
}
function isListFamily(layoutId) {
    return layoutId.includes('-list/');
}
/**
 * A node's content with everything it borrows from elsewhere in the map filled in: a chart that
 * reads its numbers from a table is given the table's numbers, formulas worked out.
 */
function resolveContent(content, document, format) {
    if (content?.kind !== 'chart' || content.source === undefined)
        return content;
    return resolveChart(content, (nodeId) => {
        const other = document.nodes[nodeId];
        const table = other === undefined ? undefined : contentOf(other);
        return table?.kind === 'table' ? table : undefined;
    }, format);
}
/** Primitives for content — and, for a chart, the numbers it drew and the marks a tap finds. */
function drawnContent(content, rect, context) {
    if (content.kind !== 'chart')
        return { primitives: contentPrimitives(content, rect, context) };
    const drawing = drawChart(content, rect, context);
    return { primitives: drawing.primitives, chart: content, marks: drawing.marks };
}
export function buildScene(document, options) {
    const brand = brandOf(document);
    const brandColor = (depth) => brand === undefined || brand.colors.length === 0
        ? undefined
        : brand.colors[depth % brand.colors.length];
    const base = {
        ...(options.baseTextStyle ?? DEFAULT_TEXT_STYLE),
        ...(brand === undefined ? {} : { fontFamily: brand.font }),
    };
    const maxTextWidth = options.maxTextWidth ?? DEFAULT_MAX_TEXT_WIDTH;
    const padding = options.padding ?? DEFAULT_NODE_PADDING;
    const selected = options.selected ?? new Set();
    const visible = visibleNodes(document);
    const hasChildren = new Set();
    for (const node of Object.values(document.nodes)) {
        if (node.parentId !== null)
            hasChildren.add(node.parentId);
    }
    const familyOf = familyResolver(document);
    const nodes = [];
    // Each node's words as measured, without a box: a structure hangs some names beside a mark.
    const textSizes = new Map();
    for (const [nodeId, depth] of visible) {
        const node = document.nodes[nodeId];
        if (node === undefined)
            continue;
        const content = resolveContent(contentOf(node), document, options.numberFormat);
        const link = mapLinkOf(node);
        const rich = content !== undefined || link !== undefined;
        const context = {
            measurer: options.measurer,
            base,
            ...(options.numberFormat === undefined ? {} : { format: options.numberFormat }),
        };
        // Text on a line: a node in a `simple-` family that nobody gave a shape of its own. The root
        // stays a box — it is what the whole map hangs from. A node with content is always a box: a
        // picture standing on an underline reads as something that fell off the map.
        const underlined = !rich &&
            node.parentId !== null &&
            node.shape === DEFAULT_NODE_SHAPE &&
            isSimpleFamily(familyOf(node.parentId));
        const textStyle = textStyleFor(node.style, base);
        const plain = toPlainText(node.richText);
        const box = content === undefined ? undefined : contentSize(content, context);
        // A picture with no caption is only the picture; an empty line under it would be a gap.
        const showText = !(content !== undefined && plain.trim() === '');
        const measured = options.measurer.measure(
        // `plainText` rather than the rich text: measuring is about extents, and marks change how
        // text looks without changing where it wraps at this level of fidelity. Rich rendering is
        // a renderer concern.
        plain, textStyle, 
        // A caption wraps to the width of what it captions, when that is wider.
        box === undefined ? maxTextWidth : Math.max(maxTextWidth, box.width));
        textSizes.set(node.id, { width: measured.width, height: measured.height });
        // A node the layout has sized keeps that size; one it has not is sized to its content, so a
        // freshly created node is not a zero-by-zero point nobody can click.
        /*
         * A shape whose interior is smaller than its box needs a bigger box.
         *
         * Text is centred in the *box*, and a diamond's interior is half of one — so the same words
         * that fit a rectangle spill out the sides of a diamond drawn around the same rectangle.
         * Growing the box is the honest fix: the layout then reserves the room the shape actually
         * occupies, rather than the shape being drawn larger than the space it was given.
         */
        // Content sits in a card. A table inside a diamond would spill out of every corner, so a
        // shape with no room for a rectangle inside it is drawn as a rounded one while it holds some.
        const shape = rich && !BOXY_SHAPES.has(node.shape) ? 'rounded-rectangle' : node.shape;
        const scale = paddingScaleFor(shape);
        let width;
        let height;
        let textBox;
        let contentRect;
        let linkRect;
        const lines = showText ? measured.lines : [];
        if (!rich) {
            width =
                node.width > 0
                    ? node.width
                    : Math.max(MIN_NODE_WIDTH, measured.width + padding.x * 2 * scale.x);
            height =
                node.height > 0
                    ? node.height
                    : Math.max(MIN_NODE_HEIGHT, measured.height + padding.y * 2 * scale.y);
        }
        else {
            const chip = link === undefined ? 0 : linkWidth(link, context);
            const textWidth = showText ? measured.width + padding.x * 2 : 0;
            const textHeight = showText ? measured.height + padding.y * 2 : 0;
            width = Math.max(MIN_NODE_WIDTH, node.width, textWidth, (box?.width ?? 0) + CONTENT_INSET * 2, chip + CONTENT_INSET * 2);
            const top = box === undefined ? 0 : CONTENT_INSET + box.height;
            const bottom = link === undefined ? 0 : LINK_HEIGHT + CONTENT_INSET;
            // Between the parts, a gap; with no text, the content and the chip still need one.
            const gaps = box !== undefined && !showText && link !== undefined ? CONTENT_GAP : 0;
            height = Math.max(MIN_NODE_HEIGHT, node.height, top + textHeight + bottom + gaps + (box !== undefined && !showText ? CONTENT_INSET : 0));
            if (box !== undefined) {
                contentRect = {
                    x: node.x + (width - box.width) / 2,
                    y: node.y + CONTENT_INSET,
                    width: box.width,
                    height: box.height,
                };
            }
            if (showText) {
                textBox = { x: node.x, y: node.y + top, width, height: textHeight };
            }
            if (link !== undefined) {
                linkRect = {
                    x: node.x + (width - chip) / 2,
                    y: node.y + height - CONTENT_INSET - LINK_HEIGHT,
                    width: chip,
                    height: LINK_HEIGHT,
                };
            }
        }
        const paletteColor = brandColor(depth) ?? mapPaletteColor(document.map.themeId, depth);
        const rect = { x: node.x, y: node.y, width, height };
        nodes.push({
            id: node.id,
            rect,
            lines,
            textStyle,
            shape,
            // Spread rather than assigned: under `exactOptionalPropertyTypes` an explicit `undefined`
            // is not the same as an absent key, and the scene should not carry an emoji-shaped hole.
            ...(node.emoji === undefined ? {} : { emoji: node.emoji }),
            cornerRadius: cornerRadiusFor(shape, node.style, rich ? Math.min(height, 24) : height),
            outline: underlined ? underlineOutline(rect) : outlineFor(shape, rect),
            ...(tooltipOf(node) === '' ? {} : { tooltip: tooltipOf(node) }),
            style: paletteColor === undefined ? node.style : { stroke: paletteColor, ...node.style },
            depth,
            collapsed: node.collapsed,
            attachmentCount: node.attachments?.length ?? 0,
            hasChildren: hasChildren.has(node.id),
            hasHiddenChildren: node.collapsed && hasChildren.has(node.id),
            selected: selected.has(node.id),
            ...(textBox === undefined ? {} : { textBox }),
            ...(content === undefined || contentRect === undefined
                ? {}
                : {
                    content: {
                        kind: content.kind,
                        rect: contentRect,
                        ...drawnContent(content, contentRect, context),
                        ...(content.kind === 'image' || content.kind === 'audio'
                            ? { attachmentId: content.attachmentId }
                            : content.kind === 'video' && content.attachmentId !== undefined
                                ? { attachmentId: content.attachmentId }
                                : {}),
                        ...(content.kind === 'video' && content.url !== undefined
                            ? { url: content.url }
                            : {}),
                    },
                }),
            ...(link === undefined || linkRect === undefined
                ? {}
                : {
                    link: {
                        mapId: link.mapId,
                        title: link.title,
                        rect: linkRect,
                        primitives: linkPrimitives(link, linkRect, context),
                    },
                }),
        });
    }
    // A structure — a metro map, a timeline, a board — draws its own picture around the nodes and
    // gives some of them a part to play in it.
    const layoutId = document.map.layoutId;
    const structure = isStructureLayout(layoutId)
        ? structureScene(layoutId, { document, nodes, text: textSizes })
        : null;
    const drawn = structure === null ? nodes : [...structure.nodes];
    // Painter's order: shallow first, so a deep node drawn over its ancestor looks deliberate
    // rather than accidental. Ties broken by id so the order is stable across rebuilds — an
    // unstable order makes every rebuild look like a change to a renderer diffing by index.
    drawn.sort((a, b) => (a.depth === b.depth ? a.id.localeCompare(b.id) : a.depth - b.depth));
    const rects = new Map();
    for (const node of drawn)
        rects.set(node.id, node.rect);
    const canvas = mapCanvas(document.map.themeId);
    const underlay = structure?.underlay ?? [];
    const nodeBounds = boundsOf(drawn);
    const underlayBounds = primitivesBounds(underlay);
    return {
        nodes: drawn,
        connections: buildConnections(document, visible, rects, familyOf, structure?.link),
        bounds: underlayBounds === null || drawn.length === 0
            ? nodeBounds
            : unionRect(nodeBounds, underlayBounds),
        ...(canvas === undefined ? {} : { canvas }),
        ...(underlay.length === 0 ? {} : { underlay }),
    };
}
function unionRect(a, b) {
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    return {
        x,
        y,
        width: Math.max(a.x + a.width, b.x + b.width) - x,
        height: Math.max(a.y + a.height, b.y + b.height) - y,
    };
}
/** A tree that grows up or down, whose links therefore leave and arrive vertically. */
function growsVertically(layoutId) {
    const direction = layoutId.split('/')[1];
    return direction === 'down' || direction === 'up' || direction === 'up-down'
        ? 'vertical'
        : undefined;
}
function buildConnections(document, visible, rects, familyFor, structureLink) {
    const connections = [];
    const colors = brandOf(document)?.colors ?? [];
    const brandColor = (depth) => colors[depth % colors.length];
    /*
     * The layout family decides how its own hierarchy links look — `curved-line-tree` and
     * `cornered-tree` are not two names for the same picture, and a fishbone is not a routing
     * style at all but a diagram with its own geometry (`fishboneRoute`). Reading it from the layout keeps the
     * renderer from having a second opinion about what those names mean.
     *
     * A branch may carry a family of its own, and then the links *inside* it are drawn that family's
     * way. Without this a branch arranged downwards would be packed downwards and still drawn with
     * the map's curves — arranged by one family and painted by another, which reads as a rendering
     * fault rather than as a choice somebody made.
     */
    const routingFor = (nodeId) => routingForLayout(familyFor(nodeId));
    // A fishbone is drawn from its head: the root's centre line is the spine every bone joins.
    const rootId = document.map.rootNodeId;
    const rootRect = rects.get(rootId);
    const spineY = rootRect === undefined ? 0 : rootRect.y + rootRect.height / 2;
    for (const node of Object.values(document.nodes)) {
        if (node.parentId === null)
            continue;
        // Both ends have to be on screen. A link into a collapsed branch would be a line to nowhere.
        if (!visible.has(node.id) || !visible.has(node.parentId))
            continue;
        const from = rects.get(node.parentId);
        const to = rects.get(node.id);
        if (from === undefined || to === undefined)
            continue;
        // Asked about the *parent*: a link belongs to the branch it hangs inside, and the child is
        // the first node of the next one down. Asking the child would draw the link into a branch
        // with the branch's own family, one link too early.
        const hierarchyRouting = routingFor(node.parentId);
        const parent = document.nodes[node.parentId];
        const family = familyFor(node.parentId);
        const underlinedParent = node.parentId !== rootId &&
            parent?.shape === DEFAULT_NODE_SHAPE &&
            parent.parentId !== null &&
            isSimpleFamily(familyFor(parent.parentId));
        const underlinedChild = node.shape === DEFAULT_NODE_SHAPE && isSimpleFamily(family);
        // A structure says which of its links are drawn at all, and which hang as an outline.
        const drawnAs = structureLink?.(node.parentId, node.id) ?? 'usual';
        if (drawnAs === 'none')
            continue;
        const path = drawnAs === 'list'
            ? listRoute(from, to, { inset: LIST_INDENT / 2, rounded: true, underlined: false })
            : family.startsWith('fishbone/')
                ? fishboneRoute(from, to, node.parentId === rootId ? 'bone' : parent?.parentId === rootId ? 'cause' : 'deeper', spineY)
                : isListFamily(family)
                    ? listRoute(from, to, {
                        inset: LIST_INDENT / 2,
                        rounded: family.startsWith('continuous-list'),
                        underlined: underlinedChild,
                    })
                    : underlinedChild
                        ? simpleRoute(from, to, hierarchyRouting, !underlinedParent, growsVertically(family) === 'vertical')
                        : route(from, to, hierarchyRouting, undefined, growsVertically(family));
        // A branch someone coloured is drawn in its colour all the way to its parent: the border and
        // the line are one stroke to the eye, and a coloured box on a grey line reads as a mistake.
        const paletteColor = node.style.stroke ??
            brandColor(visible.get(node.id) ?? 0) ??
            mapPaletteColor(document.map.themeId, visible.get(node.id) ?? 0);
        connections.push({
            id: node.id,
            kind: 'hierarchy',
            from: node.parentId,
            to: node.id,
            segments: path.segments,
            routing: hierarchyRouting,
            lineType: 'solid',
            // A tree does not need arrows: the hierarchy is legible from the shape, and an arrowhead
            // per link is a lot of ink saying something the eye already knows.
            startMarker: 'none',
            endMarker: 'none',
            startAngle: markerAngle(path.start.direction),
            endAngle: markerAngle(path.end.direction),
            midpoint: path.midpoint,
            ...(paletteColor === undefined ? {} : { color: paletteColor }),
            depth: visible.get(node.id) ?? 0,
        });
    }
    for (const edge of Object.values(document.edges)) {
        if (!visible.has(edge.sourceNodeId) || !visible.has(edge.targetNodeId))
            continue;
        const from = rects.get(edge.sourceNodeId);
        const to = rects.get(edge.targetNodeId);
        if (from === undefined || to === undefined)
            continue;
        const path = route(from, to, edge.routingType, edge.controlPoints);
        connections.push({
            id: edge.id,
            kind: 'cross-link',
            from: edge.sourceNodeId,
            to: edge.targetNodeId,
            ...(edge.label === undefined ? {} : { label: edge.label }),
            segments: path.segments,
            routing: edge.routingType,
            lineType: edge.lineType,
            startMarker: edge.startMarker,
            endMarker: edge.endMarker,
            startAngle: markerAngle(path.start.direction),
            endAngle: markerAngle(path.end.direction),
            midpoint: path.midpoint,
            ...(edge.color === undefined ? {} : { color: edge.color }),
            ...(edge.width === undefined ? {} : { width: edge.width }),
            // A cross-link belongs to neither branch, so it takes the shallower end's depth — which is
            // the one a reader's eye follows it from.
            depth: Math.min(visible.get(edge.sourceNodeId) ?? 0, visible.get(edge.targetNodeId) ?? 0),
        });
    }
    return connections;
}
/** The rectangle enclosing everything drawn. Empty at the origin when there is nothing. */
export function boundsOf(nodes) {
    const first = nodes[0];
    if (first === undefined)
        return { x: 0, y: 0, width: 0, height: 0 };
    let minX = first.rect.x;
    let minY = first.rect.y;
    let maxX = first.rect.x + first.rect.width;
    let maxY = first.rect.y + first.rect.height;
    for (const node of nodes) {
        minX = Math.min(minX, node.rect.x);
        minY = Math.min(minY, node.rect.y);
        maxX = Math.max(maxX, node.rect.x + node.rect.width);
        maxY = Math.max(maxY, node.rect.y + node.rect.height);
    }
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
/** Where a connection meets a node: its centre, until routing arrives in module 11. */
export function anchorOf(node) {
    return { x: node.rect.x + node.rect.width / 2, y: node.rect.y + node.rect.height / 2 };
}
//# sourceMappingURL=scene.js.map